import type { DuplicateCandidate, DuplicateSide, MatchReason, MatchSignal } from '../../types/duplicates';

/* ------------------------------------------------------------------ */
/* Confidence                                                          */
/* ------------------------------------------------------------------ */

export const VERDICT_LABEL: Record<DuplicateCandidate['verdict'], string> = {
  certain: 'Certain',
  likely: 'Likely',
  possible: 'Possible',
};

/** The long form, for the one place that has room for a sentence */
export const VERDICT_SENTENCE: Record<DuplicateCandidate['verdict'], string> = {
  certain: 'Almost certainly the same',
  likely: 'Likely the same',
  possible: 'Worth a look',
};

export const verdictTone = (verdict: DuplicateCandidate['verdict']): 'red' | 'amber' | 'neutral' =>
  verdict === 'certain' ? 'red' : verdict === 'likely' ? 'amber' : 'neutral';

export const VERDICT_RANK: Record<DuplicateCandidate['verdict'], number> = { certain: 0, likely: 1, possible: 2 };

/** Strongest first — the order a reviewer wants to work in */
export const sortCandidates = (candidates: DuplicateCandidate[]): DuplicateCandidate[] =>
  [...candidates].sort(
    (a, b) =>
      Number(!!a.dismissed) - Number(!!b.dismissed) ||
      VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict] ||
      b.score - a.score,
  );

/* ------------------------------------------------------------------ */
/* Evidence                                                            */
/* ------------------------------------------------------------------ */

export const REASON_LABEL: Record<MatchReason, string> = {
  same_email: 'Same email',
  same_phone: 'Same phone',
  same_social: 'Same social link',
  same_website: 'Same website',
  identical_name: 'Identical name',
  similar_name: 'Similar names',
  initials: 'Same initials',
  shared_organization: 'Same organization',
  same_role: 'Same role',
};

const norm = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

/**
 * What a signal actually proves. When both records hold the same text there is
 * one value to show ("same phone +224 62…"); when they differ the two values
 * stand next to each other so the reviewer can judge them.
 */
export function describeSignal(signal: MatchSignal): { label: string; shared: string | null; left: string; right: string } {
  const label = REASON_LABEL[signal.reason] || 'Match';
  const same = !!signal.left && norm(signal.left) === norm(signal.right);
  return { label, shared: same ? signal.left : null, left: signal.left, right: signal.right };
}

/* ------------------------------------------------------------------ */
/* What hangs off a record                                             */
/* ------------------------------------------------------------------ */

type AttachmentKey = keyof DuplicateSide['attachments'];

const ATTACHMENT_WORDS: Record<AttachmentKey, [string, string]> = {
  assignments: ['assignment', 'assignments'],
  relationships: ['relationship', 'relationships'],
  influenceScores: ['influence score', 'influence scores'],
  media: ['media file', 'media files'],
  wikiPages: ['wiki page', 'wiki pages'],
  stances: ['stance', 'stances'],
  watches: ['watch', 'watches'],
  signals: ['signal', 'signals'],
};

const ATTACHMENT_ORDER: AttachmentKey[] = [
  'assignments',
  'relationships',
  'wikiPages',
  'media',
  'stances',
  'influenceScores',
  'signals',
  'watches',
];

export const plural = (count: number, singular: string, plural_?: string): string =>
  `${count} ${count === 1 ? singular : plural_ || `${singular}s`}`;

export const attachmentTotal = (side: DuplicateSide): number =>
  ATTACHMENT_ORDER.reduce((sum, key) => sum + (side.attachments?.[key] || 0), 0);

/** "3 assignments · 2 relationships · 1 wiki page", or nothing at all */
export function attachmentSummary(side: DuplicateSide): string {
  const parts = ATTACHMENT_ORDER.filter((key) => (side.attachments?.[key] || 0) > 0).map((key) => {
    const [one, many] = ATTACHMENT_WORDS[key];
    return plural(side.attachments[key], one, many);
  });
  return parts.join(' · ');
}

/** The same phrasing for what a merge moves: "3 assignments, 2 relationships, 1 wiki page" */
export function movedSummary(moved: Record<string, number> | undefined): string {
  const entries = Object.entries(moved || {}).filter(([, count]) => count > 0);
  if (!entries.length) return 'Nothing to move';
  const known = ATTACHMENT_ORDER.filter((key) => (moved?.[key] || 0) > 0).map((key) => {
    const [one, many] = ATTACHMENT_WORDS[key];
    return plural(moved![key], one, many);
  });
  const extra = entries
    .filter(([key]) => !(ATTACHMENT_ORDER as string[]).includes(key))
    .map(([key, count]) => plural(count, humanize(key).toLowerCase()));
  return [...known, ...extra].join(', ');
}

/* ------------------------------------------------------------------ */
/* Fields                                                              */
/* ------------------------------------------------------------------ */

const FIELD_LABEL: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  name: 'Name',
  fullName: 'Full name',
  professionalEmail: 'Work email',
  personalEmail: 'Personal email',
  email: 'Email',
  professionalPhone: 'Work phone',
  personalPhone: 'Personal phone',
  phone: 'Phone',
  linkedinUrl: 'LinkedIn',
  twitterUrl: 'X',
  facebookUrl: 'Facebook',
  telegramUrl: 'Telegram',
  whatsapp: 'WhatsApp',
  website: 'Website',
  location: 'Location',
  category: 'Category',
  bio: 'Bio',
  role: 'Role',
  department: 'Department',
  type: 'Type',
  description: 'Description',
  acronym: 'Acronym',
  headquarters: 'Headquarters',
  foundedYear: 'Founded',
};

/** camelCase / snake_case into sentence case, for any field the map does not know */
export function humanize(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '';
  return words
    .map((word, index) => {
      // Acronyms keep their shape: "profileImageURL" → "Profile image URL"
      if (word.length > 1 && word === word.toUpperCase()) return word;
      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word.toLowerCase();
    })
    .join(' ');
}

export const fieldLabel = (key: string): string => FIELD_LABEL[key] || humanize(key);

export type FieldState = 'same' | 'conflict' | 'only-merged' | 'only-survivor' | 'empty';

export interface FieldRow {
  key: string;
  label: string;
  survivorValue: string;
  mergedValue: string;
  state: FieldState;
  /** Which record's value the survivor ends up with */
  choice: 'survivor' | 'merged';
  /** True when the choice was made for the reviewer because the survivor had nothing */
  automatic: boolean;
}

const text = (value: string | null | undefined): string => (value === null || value === undefined ? '' : String(value).trim());

/**
 * Field by field, what the two records hold and which value wins.
 *
 * A field the survivor has nothing in takes the other record's value on its
 * own — there is no decision to make there. Fields that agree stay quiet.
 * Only real disagreements are a choice, and the survivor keeps its own value
 * until the reviewer says otherwise.
 */
export function buildFieldRows(
  survivor: Pick<DuplicateSide, 'fields'>,
  merged: Pick<DuplicateSide, 'fields'>,
  choices: Record<string, 'survivor' | 'merged'> = {},
): FieldRow[] {
  const keys = Array.from(new Set([...Object.keys(survivor.fields || {}), ...Object.keys(merged.fields || {})]));
  return keys.map((key) => {
    const survivorValue = text(survivor.fields?.[key]);
    const mergedValue = text(merged.fields?.[key]);

    let state: FieldState;
    if (!survivorValue && !mergedValue) state = 'empty';
    else if (survivorValue && !mergedValue) state = 'only-survivor';
    else if (!survivorValue && mergedValue) state = 'only-merged';
    else if (norm(survivorValue) === norm(mergedValue)) state = 'same';
    else state = 'conflict';

    const preset = choices[key];
    const automatic = state === 'only-merged' && !preset;
    const choice: 'survivor' | 'merged' = preset || (state === 'only-merged' ? 'merged' : 'survivor');

    return { key, label: fieldLabel(key), survivorValue, mergedValue, state, choice, automatic };
  });
}

/** Rows a reviewer has to look at, and the quiet ones that agree */
export const splitRows = (rows: FieldRow[]) => ({
  decisions: rows.filter((row) => row.state === 'conflict' || row.state === 'only-merged'),
  quiet: rows.filter((row) => row.state === 'same' || row.state === 'only-survivor'),
});

/** Only the fields the survivor takes from the other record — absent means "keep mine" */
export function planFieldChoices(rows: FieldRow[]): Record<string, 'survivor' | 'merged'> {
  const choices: Record<string, 'survivor' | 'merged'> = {};
  rows.forEach((row) => {
    if (row.choice === 'merged' && row.mergedValue) choices[row.key] = 'merged';
  });
  return choices;
}

/* ------------------------------------------------------------------ */
/* Which record survives                                               */
/* ------------------------------------------------------------------ */

export interface SurvivorChoice {
  survivorId: string;
  mergedId: string;
  /** Why this one, in the words shown next to the choice */
  reason: string;
}

const filledFields = (side: DuplicateSide): number =>
  Object.values(side.fields || {}).filter((value) => text(value)).length;

const time = (iso: string): number => {
  const parsed = new Date(iso).getTime();
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

/**
 * The record with more hanging off it survives: merging into it moves the
 * least and breaks the fewest links. Ties go to the fuller record, then to
 * the one that has been there longest.
 */
export function defaultSurvivor(candidate: Pick<DuplicateCandidate, 'left' | 'right'>): SurvivorChoice {
  const { left, right } = candidate;
  const leftCount = attachmentTotal(left);
  const rightCount = attachmentTotal(right);

  if (leftCount !== rightCount) {
    const [winner, loser, winCount, loseCount] =
      leftCount > rightCount ? [left, right, leftCount, rightCount] : [right, left, rightCount, leftCount];
    return {
      survivorId: winner.id,
      mergedId: loser.id,
      reason: `More hangs off it — ${winCount} against ${loseCount}`,
    };
  }

  const leftFilled = filledFields(left);
  const rightFilled = filledFields(right);
  if (leftFilled !== rightFilled) {
    const [winner, loser] = leftFilled > rightFilled ? [left, right] : [right, left];
    return { survivorId: winner.id, mergedId: loser.id, reason: 'More of its fields are filled in' };
  }

  const [winner, loser] = time(left.createdAt) <= time(right.createdAt) ? [left, right] : [right, left];
  return { survivorId: winner.id, mergedId: loser.id, reason: 'Added first' };
}

/* ------------------------------------------------------------------ */
/* Small formatting                                                    */
/* ------------------------------------------------------------------ */

/**
 * Duplicates very often share a name, which makes any sentence with both of
 * them in it useless. When the labels match, the date the record arrived
 * stands in as the thing that tells them apart.
 */
export function labelAgainst(side: DuplicateSide, other: DuplicateSide): string {
  if (side.label.trim() !== other.label.trim()) return side.label;
  const added = shortDate(side.createdAt);
  return added ? `${side.label} (added ${added})` : side.label;
}

export function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export const entityHref = (entityType: DuplicateCandidate['entityType'], id: string): string =>
  entityType === 'organization' ? `/organizations/${id}` : `/stakeholders/${id}`;

export const ENTITY_WORD: Record<DuplicateCandidate['entityType'], [string, string]> = {
  stakeholder: ['person', 'people'],
  organization: ['organization', 'organizations'],
};
